import { useState, useMemo } from "react";

/**
 * Data column: `key` must be a real field of `T`. May be sortable.
 * Custom column: arbitrary key but must provide `render` and explicitly opt out of sorting.
 */
export type Column<T> =
  | {
      key: keyof T & string;
      header: string;
      sortable?: boolean;
      render?: (row: T) => React.ReactNode;
      align?: "left" | "right" | "center";
      hideHeader?: boolean;
    }
  | {
      key: string;
      header: string;
      sortable: false;
      render: (row: T) => React.ReactNode;
      align?: "left" | "right" | "center";
      hideHeader?: boolean;
    };

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  emptyMessage?: string;
  /** Optional row-level click handler. */
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  emptyMessage = "No data",
  onRowClick,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortKey];
      const bVal = (b as Record<string, unknown>)[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const alignClass = (a?: "left" | "right" | "center") =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  if (rows.length === 0) {
    return (
      <div className="text-center py-10 text-text-muted text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-surface border-b border-border-subtle">
            {columns.map((col) => {
              const sortable = col.sortable !== false;
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-4 py-3 font-semibold text-xs uppercase tracking-wider text-text-muted ${alignClass(
                    col.align,
                  )} ${sortable ? "cursor-pointer select-none hover:text-text-soft" : ""}`}
                  onClick={() => sortable && toggleSort(col.key)}
                >
                  {col.hideHeader ? (
                    <span className="sr-only">{col.header}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {sortable && sortKey === col.key && (
                        <span aria-hidden="true" className="text-blue-400">
                          {sortDir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const rowKey = getRowKey(row);
            const clickable = !!onRowClick;
            return (
              <tr
                key={rowKey}
                className={`border-b border-border-subtle/60 hover:bg-surface-2/40 ${clickable ? "cursor-pointer" : ""}`}
                onClick={clickable ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-text-soft ${alignClass(col.align)}`}
                  >
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
